<template>
  <div class="container-fluid py-4">
    <div class="card p-4">
      <h5 class="mb-3">
        <i class="fas fa-user-shield me-2"></i>
        Roles (Permisos + Asignación)
      </h5>

      <!-- =========================
           CREAR PERMISO + ASIGNAR ROLES
      ========================== -->
      <div class="card p-3 mb-4" style="background: var(--q-card-alt) !important; border-color: var(--q-border) !important;">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <h6 class="mb-0">Crear permiso</h6>
          <small class="opacity-7">Se guarda en <b>permisos</b> y se asigna en <b>rol_permiso</b></small>
        </div>

        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label">Clave (única)</label>
            <input
              v-model.trim="form.clave"
              type="text"
              class="form-control"
              placeholder="ej: roles.ver"
            />
            <small class="text-muted">Formato recomendado: <b>modulo.accion</b></small>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Nombre</label>
            <input
              v-model.trim="form.nombre"
              type="text"
              class="form-control"
              placeholder="ej: Ver roles"
            />
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Grupo</label>
            <input
              v-model.trim="form.grupo"
              type="text"
              class="form-control"
              placeholder="ej: Roles"
            />
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">Asignar a roles</label>
          <div class="row">
            <div class="col-md-3 col-6 mb-2" v-for="r in roles" :key="r.id">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="'rol_' + r.id"
                  :value="r.id"
                  v-model="form.roles"
                />
                <label class="form-check-label" :for="'rol_' + r.id">
                  {{ r.nombre }}
                </label>
              </div>
            </div>
          </div>
          <small class="text-muted">Puedes asignar a varios roles de una.</small>
        </div>

        <div class="d-flex gap-2">
          <QBtn variant="tipster"  :label="saving ? 'Guardando...' : 'Crear permiso'" :loading="saving" :disabled="saving" @click="crearPermiso()" />
          <QBtn variant="neutro"   label="Limpiar" :disabled="saving" @click="limpiarForm()" />
        </div>

        <div v-if="msg.ok" class="alert alert-success mt-3 mb-0">
          {{ msg.ok }}
        </div>
        <div v-if="msg.err" class="alert alert-danger mt-3 mb-0">
          {{ msg.err }}
        </div>
      </div>

      <!-- =========================
           LISTADO PERMISOS
      ========================== -->
      <div class="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <h6 class="mb-0">Permisos existentes</h6>

        <div class="d-flex flex-wrap gap-2">
          <input
            v-model.trim="filtroTexto"
            type="text"
            class="form-control form-control-sm"
            style="min-width:220px"
            placeholder="Buscar por clave / nombre / grupo..."
          />

         <div class="select-wrap-sm">
            <select
              v-model="filtroGrupo"
              class="form-select form-select-sm select-grupos"
            >
              <option value="">Todos los grupos</option>
              <option v-for="g in gruposUnicos" :key="g" :value="g">{{ g }}</option>
            </select>
          </div>

          <QBtn variant="tipster" :label="loading ? 'Cargando...' : 'Recargar'" :loading="loading" :disabled="loading" @click="cargarTodo()" />
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-sm align-middle">
          <thead>
            <tr>
              <th style="width:60px">#</th>
              <th style="width:80px" class="th-sort" @click="sortBy('id')">
                ID <span class="sort-icon">{{ sortCol === 'id' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </th>
              <th class="th-sort" @click="sortBy('clave')">
                Clave <span class="sort-icon">{{ sortCol === 'clave' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </th>
              <th class="th-sort" @click="sortBy('nombre')">
                Nombre <span class="sort-icon">{{ sortCol === 'nombre' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </th>
              <th class="th-sort" @click="sortBy('grupo')">
                Grupo <span class="sort-icon">{{ sortCol === 'grupo' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </th>
              <th>Roles</th>
              <th style="width:140px" class="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, idx) in permisosFiltrados" :key="p.id">
              <td>{{ idx + 1 }}</td>
              <td>{{ p.id }}</td>
              <td><code>{{ p.clave }}</code></td>
              <td>{{ p.nombre }}</td>
              <td>{{ p.grupo }}</td>
              <td>
                <span v-if="p.roles">{{ p.roles }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end acciones-permiso">
              <button
                class="btn-icon-accion editar me-2"
                title="Gestionar permiso"
                @click="abrirEditar(p)"
              >
                <i class="fas fa-pen-to-square"></i>
              </button>
            </td>
            </tr>

            <tr v-if="!loading && permisosFiltrados.length === 0">
              <td colspan="7" class="text-center text-muted py-4">
                No hay permisos que coincidan.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- =========================
           MODAL EDITAR PERMISO
      ========================== -->
      <div v-if="edit.open" class="modal-backdrop-q" @click.self="cerrarEditar()">
        <div class="modal-card-q">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Editar permiso</h6>
            <button class="btn-close-q" @click="cerrarEditar()">✕</button>
          </div>

          <div class="mb-3">
            <label class="form-label">Clave (no editable)</label>
            <input class="form-control" :value="edit.data.clave" disabled />
          </div>

          <div class="mb-3">
            <label class="form-label">Nombre</label>
            <input class="form-control" v-model.trim="edit.data.nombre" />
          </div>

          <div class="mb-3">
            <label class="form-label">Grupo</label>
            <input class="form-control" v-model.trim="edit.data.grupo" />
          </div>
          <div class="mb-3">
            <label class="form-label">Roles con acceso</label>
            <div class="row">
              <div class="col-md-4 col-6 mb-2" v-for="r in roles" :key="'edit_rol_' + r.id">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'edit_rol_' + r.id"
                    :value="r.id"
                    v-model="edit.data.roles"
                  />
                  <label class="form-check-label" :for="'edit_rol_' + r.id">
                    {{ r.nombre }}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex gap-2 justify-content-end">
            <QBtn variant="neutro"   label="Cancelar" :disabled="edit.saving" @click="cerrarEditar()" />
            <QBtn variant="confirma" :label="edit.saving ? 'Guardando...' : 'Guardar'" :loading="edit.saving" :disabled="edit.saving" @click="guardarEditar()" />
          </div>

          <div v-if="edit.err" class="alert alert-danger mt-3 mb-0">{{ edit.err }}</div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/services/apiClient";

export default {
  name: "RolesView",
  data() {
    return {
      loading: false,
      saving: false,
      roles: [],
      permisos: [],

      form: {
        clave: "",
        nombre: "",
        grupo: "General",
        roles: [], // ids
      },

      filtroTexto: "",
      filtroGrupo: "",
      sortCol: "id",
      sortDir: "asc",

      msg: { ok: "", err: "" },

      edit: {
        open: false,
        saving: false,
        err: "",
        data: {
          id: null,
          clave: "",
          nombre: "",
          grupo: "",
          roles: [],
        },
      },
    };
  },
  computed: {
    gruposUnicos() {
      const set = new Set(this.permisos.map((p) => p.grupo).filter(Boolean));
      return Array.from(set).sort();
    },
    permisosFiltrados() {
      const t = (this.filtroTexto || "").toLowerCase();
      const g = this.filtroGrupo;

      const lista = this.permisos.filter((p) => {
        const okGrupo = !g || p.grupo === g;
        const okTexto =
          !t ||
          String(p.id).includes(t) ||
          (p.clave || "").toLowerCase().includes(t) ||
          (p.nombre || "").toLowerCase().includes(t) ||
          (p.grupo || "").toLowerCase().includes(t);
        return okGrupo && okTexto;
      });

      const col = this.sortCol;
      const dir = this.sortDir === "asc" ? 1 : -1;
      return [...lista].sort((a, b) => {
        const va = col === "id" ? (a.id ?? 0) : String(a[col] ?? "").toLowerCase();
        const vb = col === "id" ? (b.id ?? 0) : String(b[col] ?? "").toLowerCase();
        if (va < vb) return -1 * dir;
        if (va > vb) return  1 * dir;
        return 0;
      });
    },
  },
  mounted() {
    this.cargarTodo();
  },
  methods: {
    async cargarTodo() {
      this.msg.ok = "";
      this.msg.err = "";
      this.loading = true;

      try {
        const [rolesRes, permisosRes] = await Promise.all([
          api.get("/api/v2/roles"),
          api.get("/api/v2/permisos"),
        ]);
        const rolesPayload = rolesRes?.data;
        const permisosPayload = permisosRes?.data;

        this.roles = Array.isArray(rolesPayload)
          ? rolesPayload
          : Array.isArray(rolesPayload?.data)
            ? rolesPayload.data
            : Array.isArray(rolesPayload?.roles)
              ? rolesPayload.roles
              : Array.isArray(rolesPayload?.data?.roles)
                ? rolesPayload.data.roles
                : [];

        this.permisos = Array.isArray(permisosPayload)
          ? permisosPayload
          : Array.isArray(permisosPayload?.data)
            ? permisosPayload.data
            : Array.isArray(permisosPayload?.permisos)
              ? permisosPayload.permisos
              : [];
      } catch (e) {
        this.msg.err = this.getErr(e) || "Error cargando roles/permisos";
      } finally {
        this.loading = false;
      }
    },

    limpiarForm() {
      this.form = { clave: "", nombre: "", grupo: "General", roles: [] };
      this.msg.ok = "";
      this.msg.err = "";
    },

    validarForm() {
      if (!this.form.clave || this.form.clave.length < 3) return "La clave es obligatoria (mínimo 3 caracteres).";
      if (!/^[a-z0-9_.-]+$/i.test(this.form.clave)) return "La clave solo puede tener letras/números y . _ -";
      if (!this.form.nombre || this.form.nombre.length < 3) return "El nombre es obligatorio (mínimo 3 caracteres).";
      if (!this.form.grupo || this.form.grupo.length < 2) return "El grupo es obligatorio.";
      return null;
    },

    async crearPermiso() {
      this.msg.ok = "";
      this.msg.err = "";

      const err = this.validarForm();
      if (err) {
        this.msg.err = err;
        return;
      }

      this.saving = true;
      try {
        // ✅ crea permiso y asigna a roles en el mismo request
        const payload = {
          clave: this.form.clave,
          nombre: this.form.nombre,
          grupo: this.form.grupo,
          roles: this.form.roles, // [1,2,...]
        };

        await api.post("/api/v2/permisos", payload);

        this.msg.ok = "Permiso creado y asignado correctamente ✅";
        this.limpiarForm();
        await this.cargarTodo();
      } catch (e) {
        this.msg.err = this.getErr(e) || "Error creando permiso";
      } finally {
        this.saving = false;
      }
    },

    abrirEditar(p) {
      this.edit.err = "";
      this.edit.open = true;

      let rolesAsignados = [];

      if (Array.isArray(p.roles_ids)) {
        rolesAsignados = p.roles_ids.map((x) => Number(x));
      } else if (Array.isArray(p.roles)) {
        rolesAsignados = p.roles.map((x) => Number(x.id ?? x));
      }

      this.edit.data = {
        id: p.id,
        clave: p.clave,
        nombre: p.nombre,
        grupo: p.grupo,
        roles: rolesAsignados,
      };
    },
    cerrarEditar() {
      this.edit.open = false;
      this.edit.err = "";
      this.edit.data = { id: null, clave: "", nombre: "", grupo: "", roles: [] };
    },

    async guardarEditar() {
      this.edit.err = "";
      if (!this.edit.data.nombre || this.edit.data.nombre.length < 3) {
        this.edit.err = "Nombre inválido (mínimo 3 caracteres).";
        return;
      }
      if (!this.edit.data.grupo || this.edit.data.grupo.length < 2) {
        this.edit.err = "Grupo inválido.";
        return;
      }

      this.edit.saving = true;
      try {
        await api.put(`/api/v2/permisos/${this.edit.data.id}`, {
          nombre: this.edit.data.nombre,
          grupo: this.edit.data.grupo,
          roles: this.edit.data.roles,
        });

        this.cerrarEditar();
        await this.cargarTodo();
      } catch (e) {
        this.edit.err = this.getErr(e) || "Error guardando cambios";
      } finally {
        this.edit.saving = false;
      }
    },

    sortBy(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortCol = col;
        this.sortDir = "asc";
      }
    },

    getErr(e) {
      return (
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (typeof e?.response?.data === "string" ? e.response.data : "") ||
        e?.message ||
        ""
      );
    },
  },
};
</script>

<style scoped>
/* Botón cerrar modal */
.btn-close-q {
  background: var(--q-hover);
  border: 1px solid var(--q-border-md);
  color: var(--q-muted);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  transition: background .15s, color .15s;
}
.btn-close-q:hover {
  background: var(--q-red-soft);
  border-color: var(--q-red);
  color: var(--q-red);
}

/* code inline — claves de permiso */
:deep(code), code {
  background: var(--q-hover);
  color: var(--q-gold);
  font-family: var(--q-font-mono);
  font-size: 0.8rem;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Modal simple (sin depender de tu componente modal actual) */
.modal-backdrop-q {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}
.modal-card-q {
  width: 100%;
  max-width: 680px;
  background: var(--q-card);
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
}
.acciones-permiso {
  white-space: nowrap;
}

.btn-icon-accion {
  border: none;
  background: transparent;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.btn-icon-accion i {
  font-size: 15px;
  line-height: 1;
}

.btn-icon-accion.editar {
  color: var(--q-muted);
}

.btn-icon-accion.asignar {
  color: #d63384;
}

.btn-icon-accion:hover {
  background: var(--q-hover);
  transform: scale(1.08);
}
.select-wrap-sm {
  position: relative;
  display: inline-block;
  min-width: 190px;
}

.select-wrap-sm::after {
  content: "▼";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-52%);
  font-size: 10px;
  color: var(--q-muted);
  pointer-events: none;
  z-index: 2;
}

.select-grupos {
  min-width: 190px;
  padding-right: 28px !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  background-image: none !important;
}

.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  color: var(--q-gold);
}
.sort-icon {
  font-size: 0.75em;
  opacity: 0.5;
  margin-left: 4px;
}
.th-sort:hover .sort-icon,
.th-sort .sort-icon:not(:only-child) {
  opacity: 0.9;
}
</style>